import { Page } from "components/Page"
import { FlotillaHeader } from "components/FlotillaHeader"
import { Publicity } from "components/Publicity"
import { TwoColLayout } from "components/TwoColLayout"
import { Wrapper } from "components/Wrapper"
import * as React from "react"
import cx from "classnames"
import { Text } from "components/Text"
import { Price } from "components/Price"
import { BottomBar } from "components/BottomBar"
import { Button } from "components/Button"
import { CartPlus, ChevronRight } from "components/Icon"
import { useTranslator } from "components/Translator"
import { useConnectedUser } from "components/ConnectedUserProvider"
import { RequireLogin } from "components/RequireLogin"
import { Modal, ModalContent, ModalActions } from "components/Modal"
import { useBreakpoints } from "hooks/useBreakpoints"
import * as ProductType from "enums/ProductType"
import { Route } from "components/Route"
import { Separator } from "components/Separator"
import * as FlotillaDetailType from "enums/FlotillaDetailType"
import { HashTabs, TabList, Tab, TabPanel } from "components/Tab"
import * as AdvertType from "enums/AdvertType"
import { Avatar } from "components/Avatar"
import { RawLink } from "components/Link"
import { AdvertInteractionsProvider } from "domains/advert/AdvertInteractionsProvider"
import { Card, CardContent } from "components/Card"
import { TestimonyCard } from "domains/testimony/TestimonyCard"
import * as styles from "./styles.css?module"
import { Input } from "components/Input"
import { Image, getImageSrc } from "components/Image"
import { Discount } from "components/Discount"
import NoImagePlaceholder from "components/NoImagePlaceholder/NoImagePlaceholder.png"
import { ArrowLeft } from "components/Icon"
import { useCart } from "components/CartProvider"
import { Map, MapboxProvider, MapModal, Marker } from "components/Map"
import { ShareModal } from "components/ShareModal"
import { ShareAlt } from "components/Icon/ShareAlt"
import { UserAvatar } from "domains/user/UserAvatar"
import { BoatAvatar } from "domains/boat/BoatAvatar"
import first from "lodash/first"
import last from "lodash/last"
import { Tag } from "components/Tag"
import { Markdown } from "components/Markdown"
import { RelatedProducts } from "components/RelatedProducts"
import { ExternalUserModal } from "domains/user/ExternalUserModal"

export function ClubProductShowPage({ product, adverts, testimonies }) {
  const [type] = product.types

  switch (type.enum_value) {
    case ProductType.FLOTILLA:
      return (
        <FlotillaProductPage
          product={product}
          adverts={adverts}
          testimonies={testimonies}
        />
      )

    default:
      return <GenericProductPage product={product} />
  }
}

const GenericProductPage = ({ product }) => {
  const translator = useTranslator()

  return (
    <Page padded>
      <div className="pb-20">
        <Wrapper>
          <div className="space-y-12">
            <div className="space-y-4">
              <Button
                href={{ route: "app_club_shop_product_index" }}
                variant="text"
                color="secondary"
                iconLeft={<ArrowLeft className="w-4" />}
              >
                {translator.trans("club.product.show.back", null, "pages")}
              </Button>
              <section className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-8">
                <div className="lg:col-span-2">
                  <ProductImages product={product} />
                </div>
                <div className="space-y-6">
                  <Text variant="headline2" tag="h1">
                    {product.title}
                  </Text>
                  <div className="lg:flex lg:flex-col lg:space-y-2">
                    <Text
                      variant="headline3"
                      className="lg:flex lg:items-center text-primary-darker"
                    >
                      <ProductPrice product={product} />
                    </Text>
                    <div className="hidden lg:block">
                      <AddToCartButton product={product} />
                    </div>
                  </div>
                  <Markdown>{product.description}</Markdown>
                </div>
              </section>
            </div>
            {product.relatedProducts.length > 0 ? (
              <>
                <Separator />
                <RelatedProducts products={product.relatedProducts} />
              </>
            ) : null}
          </div>
        </Wrapper>
      </div>
      <div className="lg:hidden block">
        <BottomBar>
          <div className="flex flex-col">
            <AddToCartButton product={product} />
          </div>
        </BottomBar>
      </div>
    </Page>
  )
}

const ProductImages = ({ product }) => {
  const images = [
    product.imageFile,
    ...product.images.map((image) => image.imageFile),
  ]
  const [currentImage, setCurrentImage] = React.useState(images[0])

  return (
    <div className="lg:flex lg:flex-row-reverse lg:space-y-0 space-y-2">
      <div className="bg-white grow rounded-lg overflow-hidden">
        <img
          src={currentImage.url}
          alt=""
          className="mx-auto"
          loading={"lazy"}
        />
      </div>
      {images.length > 1 ? (
        <div className="flex lg:flex-col overflow-x-auto lg:overflow-x-hidden py-1 lg:py-0 lg:w-32 shrink-0 flex-row lg:space-y-2 space-x-2 lg:pr-2">
          {images.map((image) => (
            <button
              key={image.url}
              type="button"
              className={cx(
                "bg-white shrink-0 grow-0 rounded-lg overflow-hidden p-1 focus:outline-hidden hover:ring-2 hover:ring-primary-light focus:ring-2 focus:ring-primary-dark",
                {
                  "ring-2 ring-primary-default": image === currentImage,
                },
              )}
              onClick={() => setCurrentImage(image)}
              disabled={image === currentImage}
            >
              <img
                src={image.url}
                alt=""
                className="h-20 lg:w-full object-contain object-center"
                loading={"lazy"}
              />
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}

const canAddProductToCart = (product) => {
  if (product.variants.length > 0) {
    return product.variants.some(
      (variant) => variant.stockQuantity === null || variant.stockQuantity > 0,
    )
  }

  return true
}

const AddToCartButton = ({ product }) => {
  const connectedUser = useConnectedUser()
  const canAddToCart = canAddProductToCart(product)
  const { lg } = useBreakpoints()

  const [showRestrictedToClubModal, setShowRestrictedToClubModal] =
    React.useState(false)
  const [showAddToCartModal, setShowAddToCartModal] = React.useState(false)

  const buttonProps = {
    iconLeft: <CartPlus className="w-5" />,
    disabled: !canAddToCart,
    fullWidth: !lg,
  }

  if (product.restrictedToClub && !connectedUser?.roles.includes("ROLE_CLUB")) {
    buttonProps.onClick = () => setShowRestrictedToClubModal(true)
  } else {
    if (product.purchasable) {
      buttonProps.onClick = () => setShowAddToCartModal(true)
    } else {
      buttonProps.href = product.purchaseExternalUrl
      buttonProps.external = true
    }
  }

  return (
    <>
      <RequireLogin>
        <Button {...buttonProps}>
          <AddToCartButtonLabel product={product} user={connectedUser} />
        </Button>
      </RequireLogin>
      <RestrictedToClubModal
        isOpen={showRestrictedToClubModal}
        onRequestClose={() => setShowRestrictedToClubModal(false)}
      />
      <AddToCartModal
        isOpen={showAddToCartModal}
        product={product}
        onRequestClose={() => setShowAddToCartModal(false)}
      />
    </>
  )
}

const AddToCartButtonLabel = ({ product }) => {
  const translator = useTranslator()

  if (!product.purchasable) {
    return translator.trans("club.product.show.benefitOffer", null, "pages")
  }

  if (product.variants) {
    if (product.variants.length === 1) {
      const [variant] = product.variants

      if (variant.stockQuantity === null || variant.stockQuantity > 0) {
        return translator.trans("club.product.show.addToCart", null, "pages")
      } else {
        return translator.trans("club.product.show.outOfStock", null, "pages")
      }
    }

    if (!product.variants.some((variant) => variant.stock)) {
      return translator.trans("club.product.show.outOfStock", null, "pages")
    }
  }

  return translator.trans("club.product.show.addToCart", null, "pages")
}

const ProductPrice = ({ product }) => {
  const [price] = product.prices

  if (price) {
    return (
      <Price
        amount={price.price.amount}
        currency={price.price.currency.enum_value}
      />
    )
  }

  return <Discount amount={product.purchaseExternalDiscount} />
}

const RestrictedToClubModal = ({ isOpen, onRequestClose }) => {
  const translator = useTranslator()

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} title={""}>
      <ModalContent>
        <Text tag="p">
          {translator.trans(
            "club.product.show.restrictedToClubModal.content",
            null,
            "pages",
          )}
        </Text>
      </ModalContent>
      <ModalActions>
        <Button href={{ route: "app_club_shop_plan_index" }}>
          {translator.trans(
            "club.product.show.restrictedToClubModal.cta",
            null,
            "pages",
          )}
        </Button>
        <Button
          type="button"
          onClick={onRequestClose}
          variant="text"
          color="secondary"
        >
          {translator.trans(
            "club.product.show.restrictedToClubModal.cancel",
            null,
            "pages",
          )}
        </Button>
      </ModalActions>
    </Modal>
  )
}

const AddToCartModal = ({ isOpen, onRequestClose, product }) => {
  const translator = useTranslator()
  const cart = useCart()

  const variantsWithSize = product.variants.filter(
    (variant) => variant.size !== null,
  )

  const [selectedVariant, setSelectedVariant] = React.useState(null)

  const handleSubmit = async (e) => {
    e.preventDefault()

    const form = e.target

    const quantity = form.elements.quantity?.value ?? 1

    const productVariantId =
      variantsWithSize.length > 0
        ? form.elements.variant.value
        : product.variants[0].id

    const productPriceId = product.prices[0].id

    await cart.addProduct({ productVariantId, productPriceId, quantity })
    onRequestClose()
  }

  function handleModalClose() {
    onRequestClose()
    setSelectedVariant(null)
  }

  return (
    <Modal
      isOpen={isOpen}
      title={translator.trans(
        "club.product.show.addToCartModal.title",
        null,
        "pages",
      )}
      onRequestClose={() => handleModalClose()}
    >
      <ModalContent>
        <div className="space-y-4">
          <div className="flex space-x-2 items-center">
            <div className="w-20 h-20 border border-grey-light rounded-xs shrink-0 grow-0">
              <Image
                imageFile={product.imageFile}
                size="w640"
                alt=""
                className="w-full h-full object-center object-contain"
              />
            </div>
            <div className="grow space-y-2">
              <Text variant="headline4" className="text-dark">
                {product.title}
              </Text>
              <Text variant="headline5" className="text-primary-dark">
                <ProductPrice product={product} />
              </Text>
            </div>
          </div>
          <form id="add-to-cart" onSubmit={handleSubmit} noValidate>
            <div className="space-y-4">
              <div className="space-y-1">
                <Text variant="body2" tag="label" htmlFor="quantity">
                  {translator.trans(
                    "club.product.show.addToCartModal.quantity",
                    null,
                    "pages",
                  )}
                </Text>
                <Input
                  type="number"
                  name="quantity"
                  id="quantity"
                  defaultValue="1"
                  min="1"
                  step={1}
                />
              </div>
              {variantsWithSize.length > 0 ? (
                <fieldset className="space-y-1">
                  <Text variant="body2" tag="legend">
                    {translator.trans(
                      "club.product.show.addToCartModal.size",
                      null,
                      "pages",
                    )}
                  </Text>
                  <div className="flex">
                    {variantsWithSize.map((variant, index) => {
                      return (
                        <div key={variant.id} className="grow">
                          <VariantChoice
                            variant={variant}
                            position={
                              index === 0
                                ? "first"
                                : index === variantsWithSize.length - 1
                                  ? "last"
                                  : "middle"
                            }
                            onChange={setSelectedVariant}
                          />
                        </div>
                      )
                    })}
                  </div>
                </fieldset>
              ) : null}
            </div>
          </form>
        </div>
      </ModalContent>
      <ModalActions>
        <Button
          type="submit"
          form="add-to-cart"
          fullWidth
          busy={cart.status === "loading"}
          disabled={variantsWithSize.length > 0 && !selectedVariant}
        >
          {translator.trans(
            "club.product.show.addToCartModal.submit",
            null,
            "pages",
          )}
        </Button>
      </ModalActions>
    </Modal>
  )
}

const VariantChoice = ({ variant, position, onChange }) => {
  return (
    <div className={styles.VariantChoice} data-position={position}>
      <input
        type="radio"
        name="variant"
        id={`variant-${variant.id}`}
        className="hidden"
        onChange={(e) => onChange(e.target.value)}
        disabled={variant.stock && variant.stockQuantity === 0}
        value={variant.id}
      />
      <Text
        variant="button"
        htmlFor={`variant-${variant.id}`}
        tag="label"
        className="block w-full text-center py-2"
      >
        {variant.size.enum_label}
      </Text>
    </div>
  )
}

const FlotillaProductPage = ({ product, adverts, testimonies }) => {
  const translator = useTranslator()
  const { lg } = useBreakpoints()
  const [price] = product.prices
  const [showShareModal, setShowShareModal] = React.useState(false)

  const hotPoints = product.flotillaDetails.filter(
    (item) => item.type.enum_value === FlotillaDetailType.HOT_POINT,
  )

  const routeSteps = product.flotillaDetails.filter(
    (item) => item.type.enum_value === FlotillaDetailType.ROUTE,
  )

  const usefulPoints = product.flotillaDetails.filter(
    (item) => item.type.enum_value === FlotillaDetailType.USEFUL_POINT,
  )

  const aboardPoints = product.flotillaDetails.filter(
    (item) => item.type.enum_value === FlotillaDetailType.ABOARD_POINT,
  )

  const crewAdverts = adverts.filter(
    (advert) => advert.type.enum_value === AdvertType.CREW,
  )

  const allBoatAdverts = adverts.filter(
    (advert) => advert.type.enum_value === AdvertType.BOAT,
  )

  const boatNotFullAdverts = allBoatAdverts.filter(
    (advert) => !advert.completed,
  )

  const boatFullAdverts = allBoatAdverts.filter((advert) => advert.completed)

  const flotillaDetailsRoute = product.flotillaDetails.filter(
    (flotillaDetail) =>
      flotillaDetail.type.enum_value === FlotillaDetailType.ROUTE,
  )

  const initialViewState =
    flotillaDetailsRoute.length > 0
      ? {
          latitude: first(flotillaDetailsRoute).address.coords.lat,
          longitude: first(flotillaDetailsRoute).address.coords.lng,
          zoom: 7,
        }
      : {
          latitude: product.flotilla.departurePortAddress.coords.lat,
          longitude: product.flotilla.departurePortAddress.coords.lng,
          zoom: 14,
        }

  let urlWithoutFrom = new URL(window.location.toString())
  urlWithoutFrom.searchParams.delete("from")
  const url = urlWithoutFrom.toString()

  const subject = encodeURIComponent(
    translator.trans("ShareModal.byMail.subject", null, "components"),
  )
  const body = translator.trans(
    "ShareModal.byMail.body",
    { url: encodeURIComponent(url) },
    "components",
  )

  const handleShareClick = (e) => {
    e.stopPropagation()
    e.preventDefault()
    setShowShareModal(true)
  }

  return (
    <div className="relative">
      <Image
        imageFile={product.imageFile}
        size="w1920"
        alt=""
        className="w-full relative h-full object-cover object-center"
      />
      <button
        type="button"
        className="absolute top-4 right-4 lg:top-6 lg:right-18 flex ml-auto p-2 bg-white rounded-full"
        onClick={handleShareClick}
      >
        <ShareAlt className={cx("w-6 text-primary-dark")} />
      </button>
      <ShareModal
        isOpen={showShareModal}
        onRequestClose={() => setShowShareModal(false)}
        title={translator.trans("ShareModal.title", null, "components")}
        url={url}
        email={{ subject: subject, body: body }}
      />
      <Wrapper>
        <HashTabs defaultPanel={"schedule"}>
          <div className="relative bottom-12 lg:bottom-80 space-y-6">
            {lg ? (
              <TwoColLayout
                main={
                  <FlotillaHeader
                    className="bg-white pt-6"
                    title={product.title}
                  >
                    <div className="lg:space-y-10 space-y-6 pb-10 bg-white">
                      <section>
                        <Wrapper>
                          <div className="space-y-6">
                            <Route
                              className="pt-10"
                              steps={[
                                {
                                  place:
                                    product.flotilla.departurePortAddress
                                      .formatted,
                                  date: new Date(
                                    product.flotilla.dateInterval.from,
                                  ),
                                },
                                product.flotilla.arrivalPortAddress?.formatted
                                  ? {
                                      place:
                                        product.flotilla.arrivalPortAddress
                                          .formatted,
                                      date: new Date(
                                        product.flotilla.dateInterval.to,
                                      ),
                                    }
                                  : null,
                              ].filter(Boolean)}
                            />

                            <div className="space-y-4">
                              <div>
                                <Text variant="body2" className="text-light">
                                  {translator.trans(
                                    "club.product.show.flotilla.priceStart",
                                    null,
                                    "pages",
                                  )}
                                </Text>
                                <Text
                                  variant="headline5"
                                  className="text-primary-dark"
                                >
                                  <FlotillaPrice
                                    priceScope={product.flotilla.priceScope}
                                    price={price.price}
                                  />
                                </Text>
                              </div>
                              <AddToCartButton product={product} />
                            </div>
                          </div>
                        </Wrapper>
                      </section>
                      <Wrapper>
                        <TabList>
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.schedule.tabLabel",
                            )}
                            id={"schedule"}
                            scrollToPanel
                          />
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.participants.tabLabel",
                            )}
                            id={"participants"}
                            scrollToPanel
                          />
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.dateAndFees.tabLabel",
                            )}
                            id={"dateAndFees"}
                            scrollToPanel
                          />
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.usefulPoints.tabLabel",
                            )}
                            id={"usefulPoints"}
                            scrollToPanel
                          />
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.aboardPoints.tabLabel",
                            )}
                            id={"aboardPoints"}
                            scrollToPanel
                          />
                          <Tab
                            label={translator.trans(
                              "club.product.show.flotilla.testimonies.tabLabel",
                            )}
                            id={"testimonies"}
                            scrollToPanel
                          />
                        </TabList>
                      </Wrapper>
                      <section className="py-4">
                        <Wrapper>
                          <Text variant="body2" tag="div">
                            <p
                              dangerouslySetInnerHTML={{
                                __html: product.description,
                              }}
                            />
                          </Text>
                        </Wrapper>
                      </section>
                      <Wrapper>
                        <Separator />
                      </Wrapper>
                      <section>
                        <Wrapper>
                          <div className="space-y-6">
                            <Text variant="headline3" tag="h2">
                              {translator.trans(
                                "club.product.show.flotilla.why",
                                null,
                                "pages",
                              )}
                            </Text>
                            <ol className="space-y-4">
                              {hotPoints.map((hotPoint, index) => {
                                return (
                                  <div
                                    key={index}
                                    className="flex flex-row space-x-6"
                                  >
                                    <div className="items-center">
                                      <Image
                                        imageFile={hotPoint.imageFile}
                                        size="w-24 lg:w-12"
                                        alt=""
                                        className="shrink-0 grow-0 rounded-full inline-block"
                                      />
                                    </div>
                                    <div className="flex flex-col space-y-2">
                                      <Text variant="headline5">
                                        {hotPoint.title}
                                      </Text>
                                      <Text variant="body1">
                                        <div
                                          dangerouslySetInnerHTML={{
                                            __html: hotPoint.description,
                                          }}
                                        />
                                      </Text>
                                    </div>
                                  </div>
                                )
                              })}
                            </ol>
                          </div>
                        </Wrapper>
                      </section>
                    </div>
                  </FlotillaHeader>
                }
                sidebar={<Publicity name="vertical" />}
              />
            ) : (
              <FlotillaHeader className="bg-white pt-6" title={product.title}>
                <div className="lg:space-y-10 space-y-6 pb-10 bg-white">
                  <section>
                    <Wrapper>
                      <div className="space-y-6">
                        <Route
                          className="pt-10"
                          steps={[
                            {
                              place:
                                product.flotilla.departurePortAddress.formatted,
                              date: new Date(
                                product.flotilla.dateInterval.from,
                              ),
                            },
                            product.flotilla.arrivalPortAddress?.formatted
                              ? {
                                  place:
                                    product.flotilla.arrivalPortAddress
                                      .formatted,
                                  date: new Date(
                                    product.flotilla.dateInterval.to,
                                  ),
                                }
                              : null,
                          ].filter(Boolean)}
                        />

                        <div className="space-y-4">
                          <div>
                            <Text variant="body2" className="text-light">
                              {translator.trans(
                                "club.product.show.flotilla.priceStart",
                                null,
                                "pages",
                              )}
                            </Text>
                            <Text
                              variant="headline5"
                              className="text-primary-dark"
                            >
                              <FlotillaPrice
                                priceScope={product.flotilla.priceScope}
                                price={price.price}
                              />
                            </Text>
                          </div>
                          <AddToCartButton product={product} />
                        </div>
                      </div>
                    </Wrapper>
                  </section>
                  <Wrapper>
                    <TabList>
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.schedule.tabLabel",
                        )}
                        id={"schedule"}
                        scrollToPanel
                      />
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.participants.tabLabel",
                        )}
                        id={"participants"}
                        scrollToPanel
                      />
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.dateAndFees.tabLabel",
                        )}
                        id={"dateAndFees"}
                        scrollToPanel
                      />
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.usefulPoints.tabLabel",
                        )}
                        id={"usefulPoints"}
                        scrollToPanel
                      />
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.aboardPoints.tabLabel",
                        )}
                        id={"aboardPoints"}
                        scrollToPanel
                      />
                      <Tab
                        label={translator.trans(
                          "club.product.show.flotilla.testimonies.tabLabel",
                        )}
                        id={"testimonies"}
                        scrollToPanel
                      />
                    </TabList>
                  </Wrapper>
                  <Wrapper>
                    <Separator />
                  </Wrapper>
                  <section className="py-4">
                    <Wrapper>
                      <Text variant="body2" tag="p">
                        <div
                          dangerouslySetInnerHTML={{
                            __html: product.description,
                          }}
                        />
                      </Text>
                    </Wrapper>
                  </section>
                  <Wrapper>
                    <Separator />
                  </Wrapper>
                  <section>
                    <Wrapper>
                      <div className="space-y-6">
                        <Text variant="headline3" tag="h2">
                          {translator.trans(
                            "club.product.show.flotilla.why",
                            null,
                            "pages",
                          )}
                        </Text>
                        <ol className="space-y-4">
                          {hotPoints.map((hotPoint, index) => {
                            return (
                              <li key={index} className="space-y-1">
                                <div className="flex flex-row space-x-6">
                                  <div className="items-center">
                                    <Image
                                      imageFile={hotPoint.imageFile}
                                      size="w-12"
                                      alt=""
                                      className="rounded-full"
                                    />
                                  </div>
                                  <div className="flex flex-col space-y-2">
                                    <Text variant="headline5">
                                      {hotPoint.title}
                                    </Text>
                                    <Text variant="body1">
                                      <div
                                        dangerouslySetInnerHTML={{
                                          __html: hotPoint.description,
                                        }}
                                      />
                                    </Text>
                                  </div>
                                </div>
                              </li>
                            )
                          })}
                        </ol>
                      </div>
                    </Wrapper>
                  </section>
                </div>
              </FlotillaHeader>
            )}
            <section className={"grid gap-4 lg:gap-8"}>
              <TabList>
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.schedule.tabLabel",
                  )}
                  id={"schedule"}
                />
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.participants.tabLabel",
                  )}
                  id={"participants"}
                />
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.dateAndFees.tabLabel",
                  )}
                  id={"dateAndFees"}
                />
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.usefulPoints.tabLabel",
                  )}
                  id={"usefulPoints"}
                />
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.aboardPoints.tabLabel",
                  )}
                  id={"aboardPoints"}
                />
                <Tab
                  label={translator.trans(
                    "club.product.show.flotilla.testimonies.tabLabel",
                  )}
                  id={"testimonies"}
                />
              </TabList>
              <TabPanel id={"schedule"}>
                <div className="space-y-10">
                  <div className="flex flex-row">
                    <Button className="w-1/2" color="secondary">
                      {translator.trans(
                        "club.product.show.flotilla.buttonChoice.description",
                        null,
                        "pages",
                      )}
                    </Button>
                    <div className="w-1/2">
                      <MapboxProvider
                        accessToken={process.env.MAPBOX_ACCESS_TOKEN}
                      >
                        <MapModal
                          title={""}
                          trigger={
                            <Button
                              variant="outline"
                              className="bg-white"
                              fullWidth
                            >
                              {translator.trans(
                                "club.product.show.flotilla.buttonChoice.map",
                                null,
                                "pages",
                              )}
                            </Button>
                          }
                          map={
                            <Map initialViewState={initialViewState}>
                              {flotillaDetailsRoute.length > 0 ? (
                                flotillaDetailsRoute.map((flotillaDetail) => {
                                  return (
                                    <Marker
                                      key={flotillaDetail.id}
                                      latitude={
                                        flotillaDetail.address.coords.lat
                                      }
                                      longitude={
                                        flotillaDetail.address.coords.lng
                                      }
                                      variant={
                                        flotillaDetail ===
                                        first(flotillaDetailsRoute)
                                          ? "departure"
                                          : flotillaDetail ===
                                              last(flotillaDetailsRoute)
                                            ? "arrival"
                                            : "stopover"
                                      }
                                    />
                                  )
                                })
                              ) : (
                                <div>
                                  {product.flotilla.departurePortAddress ? (
                                    <Marker
                                      latitude={
                                        product.flotilla.departurePortAddress
                                          .coords.lat
                                      }
                                      longitude={
                                        product.flotilla.departurePortAddress
                                          .coords.lng
                                      }
                                      variant="departure"
                                    />
                                  ) : null}
                                  {product.flotilla.arrivalPortAddress ? (
                                    <Marker
                                      latitude={
                                        product.flotilla.arrivalPortAddress
                                          .coords.lat
                                      }
                                      longitude={
                                        product.flotilla.arrivalPortAddress
                                          .coords.lng
                                      }
                                      variant="arrival"
                                    />
                                  ) : null}
                                </div>
                              )}
                            </Map>
                          }
                        />
                      </MapboxProvider>
                    </div>
                  </div>
                  <Text variant="headline2">
                    {translator.trans(
                      "club.product.show.flotilla.program",
                      null,
                      "pages",
                    )}
                  </Text>
                  <IllustratedItems items={routeSteps} />
                </div>
              </TabPanel>
              <TabPanel id={"participants"}>
                <AdvertInteractionsProvider
                  ids={adverts.map((advert) => advert.id)}
                >
                  <div className="space-y-8">
                    {crewAdverts.length > 0 ? (
                      <section className="space-y-4">
                        <Text variant="headline5" tag="h3">
                          {translator.trans(
                            "club.product.show.flotilla.participants.crewTitle",
                            null,
                            "pages",
                          )}
                        </Text>
                        <CrewList>
                          {crewAdverts.map((advert) => {
                            return (
                              <CrewListItem key={advert.id}>
                                <RawLink
                                  href={{
                                    route: "app_account_advert_show",
                                    params: {
                                      userId: advert.user.id,
                                      advertId: advert.id,
                                    },
                                  }}
                                >
                                  <UserAvatar
                                    user={advert.user}
                                    alt={advert.user.fullName}
                                    size="small"
                                  />
                                </RawLink>
                              </CrewListItem>
                            )
                          })}
                        </CrewList>
                      </section>
                    ) : (
                      <section className="space-y-4">
                        <Text variant="headline5" tag="h3">
                          {translator.trans(
                            "club.product.show.flotilla.participants.crewTitle",
                            null,
                            "pages",
                          )}
                        </Text>
                        <Text>
                          {translator.trans(
                            "club.product.show.flotilla.participants.noParticipants",
                            null,
                            "pages",
                          )}
                        </Text>
                      </section>
                    )}
                    {boatNotFullAdverts.length > 0 ? (
                      <section className="space-y-4">
                        <Text variant="headline5" tag="h3">
                          {translator.trans(
                            "club.product.show.flotilla.participants.boatNotFullTitle",
                            null,
                            "pages",
                          )}
                        </Text>
                        <ul className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                          {boatNotFullAdverts.map((advert) => (
                            <li key={advert.id}>
                              <AdvertCard advert={advert} />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}
                    {boatFullAdverts.length > 0 ? (
                      <section className="space-y-4">
                        <Text variant="headline5" tag="h3">
                          {translator.trans(
                            "club.product.show.flotilla.participants.boatFullTitle",
                            null,
                            "pages",
                          )}
                        </Text>
                        <ul className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                          {boatFullAdverts.map((advert) => (
                            <li key={advert.id}>
                              <AdvertCard advert={advert} />
                            </li>
                          ))}
                        </ul>
                      </section>
                    ) : null}
                  </div>
                </AdvertInteractionsProvider>
              </TabPanel>
              <TabPanel id={"dateAndFees"}>
                <div className="space-y-8">
                  <div className="space-y-2">
                    <Text variant="headline5" tag="h3">
                      {translator.trans(
                        "club.product.show.flotilla.dateAndFees.dateTitle",
                        null,
                        "pages",
                      )}
                    </Text>
                    <Text>
                      {translator.trans(
                        "club.product.show.flotilla.dateAndFees.dateInterval",
                        {
                          from: new Date(
                            product.flotilla.dateInterval.from,
                          ).toLocaleDateString(translator.locale),
                          to: new Date(
                            product.flotilla.dateInterval.to,
                          ).toLocaleDateString(translator.locale),
                        },
                        "pages",
                      )}
                    </Text>
                  </div>
                  <div className="space-y-2">
                    <Text variant="headline5" tag="h3">
                      {translator.trans(
                        "club.product.show.flotilla.dateAndFees.priceTitle",
                        null,
                        "pages",
                      )}
                    </Text>
                    <Text>
                      <FlotillaPrice
                        priceScope={product.flotilla.priceScope}
                        price={price.price}
                      />
                    </Text>
                  </div>
                  <div className="space-y-2">
                    <Text variant="headline5" tag="h3">
                      {translator.trans(
                        "club.product.show.flotilla.dateAndFees.priceInclude",
                        null,
                        "pages",
                      )}
                    </Text>
                    <Text>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: product.flotilla.priceInclude,
                        }}
                      />
                    </Text>
                  </div>
                  <div className="space-y-2">
                    <Text variant="headline5" tag="h3">
                      {translator.trans(
                        "club.product.show.flotilla.dateAndFees.priceExclude",
                        null,
                        "pages",
                      )}
                    </Text>
                    <Text>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: product.flotilla.priceExclude,
                        }}
                      />
                    </Text>
                  </div>
                </div>
              </TabPanel>
              <TabPanel id={"usefulPoints"}>
                <IllustratedItems items={usefulPoints} />
              </TabPanel>
              <TabPanel id={"aboardPoints"}>
                <IllustratedItems items={aboardPoints} />
              </TabPanel>
              <TabPanel id={"testimonies"}>
                {testimonies.length > 0 ? (
                  <ul className="grid gap-2 md:grid-cols-2 md:gap-3 lg:grid-cols-4 lg:gap-4">
                    {testimonies.map((testimony) => {
                      return (
                        <li key={testimony.id}>
                          <TestimonyCard testimony={testimony} />
                        </li>
                      )
                    })}
                  </ul>
                ) : (
                  <Text tag="p">
                    {translator.trans(
                      "club.product.show.flotilla.testimonies.emptyMessage",
                      null,
                      "pages",
                    )}
                  </Text>
                )}
              </TabPanel>
            </section>
            {!lg ? <Publicity name="vertical" /> : null}
          </div>
        </HashTabs>
      </Wrapper>
    </div>
  )
}

const AdvertCard = ({ advert }) => {
  const translator = useTranslator()

  return (
    <Card>
      <RawLink
        href={{
          route: "app_account_advert_show",
          params: { userId: advert.user.id, advertId: advert.id },
        }}
      >
        {advert.boat?.imageFile ? (
          <CardContent>
            <Image
              imageFile={advert.boat.imageFile}
              size="w640"
              className="object-cover"
            />
          </CardContent>
        ) : (
          <img src={NoImagePlaceholder} alt="" loading={"lazy"} />
        )}
      </RawLink>
      <CardContent className="bg-primary-lighter">
        <div className="flex flex-col space-y-4">
          <RawLink
            href={{
              route: "app_account_profile_show",
              params: { userId: advert.user.id },
            }}
          >
            <div className="flex space-x-2 items-center">
              <div className="shrink-0 grow-0">
                <Avatar src={advert.user.imageFile?.url} alt="" size="small" />
              </div>
              <div className="grow">
                <Text variant="headline6">
                  {translator.trans(
                    "club.product.show.flotilla.participants.advert.userTitle",
                    null,
                    "pages",
                  )}
                </Text>
                <Text>{advert.user.fullName}</Text>
              </div>
              <div>
                <ChevronRight className="w-2" />
              </div>
            </div>
          </RawLink>
          <Separator />
          <RawLink
            href={{
              route: "app_account_boat_show",
              params: { userId: advert.user.id, boatId: advert.boat?.id },
            }}
          >
            <div className="flex space-x-2 items-center">
              <div className="shrink-0 grow-0">
                <BoatAvatar
                  boat={advert.boat}
                  src={
                    advert.boat && advert.boat.imageFile
                      ? getImageSrc(advert.boat.imageFile, "w40")
                      : null
                  }
                  alt=""
                  size="small"
                />
              </div>
              <div className="grow">
                <Text variant="headline6">
                  {translator.trans(
                    "club.product.show.flotilla.participants.advert.boatTitle",
                    null,
                    "pages",
                  )}
                </Text>
                <Text>
                  {advert.boat?.name} ({advert.boat?.brandNormalized}{" "}
                  {advert.boat?.modelNormalized})
                </Text>
              </div>
              <div>
                <ChevronRight className="w-2" />
              </div>
            </div>
          </RawLink>
        </div>
      </CardContent>
      <CardContent>
        <div className="space-y-2">
          <Text variant="headline6">
            {translator.trans(
              "club.product.show.flotilla.participants.crew",
              null,
              "pages",
            )}
          </Text>
          {advert.bookings.length > 0 ? (
            <CrewList>
              {advert.bookings.map((booking) => {
                return (
                  <CrewListItem key={booking.id}>
                    <CrewMember user={booking.user} />
                  </CrewListItem>
                )
              })}
            </CrewList>
          ) : (
            <Text tag="p">
              {translator.trans(
                "club.product.show.flotilla.participants.advert.emptyCrew",
                null,
                "pages",
              )}
            </Text>
          )}
          {advert.completed ? (
            <div>
              <Tag
                color="error"
                label={translator.trans(
                  "SearchResult.full",
                  null,
                  "components",
                )}
              />
            </div>
          ) : null}
          {!advert.completed && advert.capacityMax !== null ? (
            <Text tag="p">
              {advert.capacityMax
                ? translator.trans(
                    "club.product.show.flotilla.participants.advert.remainingSeatsNb",
                    { nb: advert.capacityMax - advert.capacityBooked },
                    "pages",
                  )
                : translator.trans(
                    "club.product.show.flotilla.participants.advert.remainingSeatsGeneric",
                    null,
                    "pages",
                  )}
            </Text>
          ) : null}
        </div>
      </CardContent>
    </Card>
  )
}

const CrewList = ({ children }) => {
  return <ul className="flex flex-wrap gap-2">{children}</ul>
}

const CrewListItem = ({ children }) => {
  return <li className="shrink-0 grow-0">{children}</li>
}

const CrewMember = ({ user }) => {
  const avatar = <UserAvatar user={user} alt={user.fullName} size="small" />
  const [showExternalUserModal, setShowExternalUserModal] =
    React.useState(false)

  if (user.roles.includes("ROLE_EXTERNAL")) {
    return (
      <React.Fragment>
        <button type={"button"} onClick={() => setShowExternalUserModal(true)}>
          {avatar}
        </button>
        <ExternalUserModal
          isOpen={showExternalUserModal}
          onRequestClose={() => setShowExternalUserModal(false)}
        />
      </React.Fragment>
    )
  }

  return (
    <RawLink
      href={{
        route: "app_account_profile_show",
        params: {
          userId: user.id,
        },
      }}
    >
      {avatar}
    </RawLink>
  )
}

const FlotillaPrice = ({ priceScope, price }) => {
  return (
    <Price
      amount={price.amount}
      currency={price.currency.enum_value}
      additionalUnit={priceScope ? `/${priceScope.enum_label}` : undefined}
    />
  )
}

const IllustratedItems = ({ items }) => {
  return (
    <ol className="space-y-6">
      {items.map((item, index) => {
        return (
          <li
            key={index}
            className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row sm:items-center sm:space-x-4"
          >
            <div className="space-y-1 sm:grow">
              <Text variant="headline5">{item.title}</Text>
              <Text>
                <div dangerouslySetInnerHTML={{ __html: item.description }} />
              </Text>
            </div>
            <div className="sm:w-1/4 sm:shrink-0 sm:grow-0">
              <Image
                imageFile={item.imageFile}
                size="w400"
                alt=""
                className="w-full rounded-sm"
              />
            </div>
          </li>
        )
      })}
    </ol>
  )
}
